import { Input, Space, Switch, Tree, Typography } from "@arco-design/web-react";
import { useMemo, useState } from "react";

import { collectTreeKeys, filterRoleTree, resolveMemberSelection } from "../service";
import type { AuthzTreeNode } from "../types";

type RoleMemberTreeProps = {
  tree: AuthzTreeNode[];
  selectedUserIds: number[];
  onChange: (userIds: number[]) => void;
};

/** 部门员工受控复选树。 */
export function RoleMemberTree({ tree, selectedUserIds, onChange }: RoleMemberTreeProps) {
  const [keyword, setKeyword] = useState("");
  const [selectedOnly, setSelectedOnly] = useState(false);
  const filteredTree = useMemo(
    () => filterRoleTree(tree, keyword, selectedOnly, new Set(selectedUserIds.map(String))),
    [keyword, selectedOnly, selectedUserIds, tree],
  );
  const treeData = useMemo(() => mapTreeData(filteredTree), [filteredTree]);
  const checkedKeys = useMemo(() => collectCheckedMemberKeys(tree, new Set(selectedUserIds)), [selectedUserIds, tree]);

  return (
    <Space direction="vertical" size={14} style={{ width: "100%" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <Input.Search
          allowClear
          aria-label="搜索部门或员工"
          placeholder="搜索部门或员工"
          style={{ width: 320 }}
          value={keyword}
          onChange={setKeyword}
        />
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Switch checked={selectedOnly} onChange={setSelectedOnly} />
          <span>仅看已选</span>
        </label>
        <Typography.Text type="secondary">已选择 {selectedUserIds.length} 名员工</Typography.Text>
      </div>
      <Tree
        blockNode
        checkable
        showLine
        checkStrictly
        treeData={treeData}
        checkedKeys={checkedKeys}
        defaultExpandedKeys={collectTreeKeys(tree)}
        onCheck={(_, extra) => {
          const node = extra.node as unknown as { key: string };
          onChange(resolveMemberSelection(tree, selectedUserIds, String(node.key), extra.checked));
        }}
      />
    </Space>
  );
}

/** 将成员节点映射为 Arco Tree 数据。 */
function mapTreeData(tree: AuthzTreeNode[]): Array<Record<string, unknown>> {
  return tree.map((node) => ({
    key: node.key,
    title: node.disabled ? `${node.title}（已停用）` : node.title,
    disabled: node.disabled && !node.userId,
    disableCheckbox: node.disabled && Boolean(node.userId),
    children: mapTreeData(node.children),
  }));
}

/** 收集当前已选员工对应的树节点 key。 */
function collectCheckedMemberKeys(tree: AuthzTreeNode[], selected: Set<number>): string[] {
  const checked: string[] = [];
  for (const node of tree) {
    if (node.userId && selected.has(node.userId)) checked.push(node.key);
    checked.push(...collectCheckedMemberKeys(node.children, selected));
  }
  return checked;
}
